import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import type {
  QuoteDetail,
  SendFromQuoteRequest,
  SendToBasketResponse,
  Vendor,
} from '@techgitter/contracts';
import { sendFromQuote } from '../../services/partOrdersApi';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { lookupVendorByUrl } from '../../utils/vendorByUrl';

interface Props {
  quote: QuoteDetail;
  vendors: Vendor[];
}

/**
 * Entry under each dialog row. `kind` switches between the two source
 * shapes the backend handles: `'item'` is a `QuoteItem.id` (with the
 * selected option's snapshot driving vendor/cost), `'repair_part'` is
 * a `RepairServiceComponent.id` (no embedded vendor — the tech picks
 * one inline).
 */
interface EligibleEntry {
  kind: 'item' | 'repair_part';
  id: string;
  primary: string;
  secondary: string;
  /**
   * Pre-resolved vendor when the backend would already pick one
   * (items+options sourced from a disti). When set, the row hides the
   * vendor picker. When null, the tech must pick a vendor before the
   * row is eligible to submit.
   */
  preselectedVendorId: string | null;
  preselectedVendorName: string | null;
  /** Surfaced under the row as a hint when the tech needs to choose. */
  supplierUrl?: string | null;
}

/**
 * Button + dialog for the manual-override flow on the quote detail page.
 *
 *  - Non-repair quotes (incl. custom builds): enumerate every item with a
 *    selected option. Disti-sourced options carry a snapshotted vendor
 *    (`sourceDistiId`, no picker needed); hand-typed / retail / inventory
 *    parts have none, so the row renders the same inline vendor picker as
 *    repair parts and stays un-submittable until a vendor is chosen.
 *  - Repair quotes: enumerate part components with
 *    `fulfillmentSource !== 'stock'`. Parts don't carry a vendor today,
 *    so the dialog renders an inline vendor picker per row; rows
 *    without a vendor stay un-submittable.
 *
 * Gated on Purchasing or Admin; renders nothing for other roles so the
 * button doesn't sit there teasing technicians.
 */
export const SendToOrderingButton: React.FC<Props> = ({ quote, vendors }) => {
  const currentUser = useCurrentUser();
  const canSend = !!currentUser?.hasAnyRole('Purchasing', 'Admin');

  // Network installs share the repair-services + part-component shape, so
  // their hardware lines are orderable the same way repair parts are.
  const isServiceShaped =
    quote.type === 'repair' || quote.type === 'network_install';

  const eligible = useMemo<EligibleEntry[]>(() => {
    const out: EligibleEntry[] = [];

    if (isServiceShaped) {
      for (const svc of quote.repairServices ?? []) {
        for (const comp of svc.components ?? []) {
          if (comp.kind === 'part') {
            if (comp.fulfillmentSource === 'stock') continue;
            out.push({
              kind: 'repair_part',
              id: comp.id,
              primary: `${svc.repairServiceName || svc.repairServiceCode}: ${
                comp.description || '(no description)'
              }`,
              secondary: comp.productCode || 'no sku',
              // Carry the vendor the tech explicitly picked on the part
              // (RepairServicesEditor writes comp.vendorId/comp.vendorName).
              // The URL-based guess in openDialog stays as a fallback for
              // parts left without a vendor.
              preselectedVendorId: comp.vendorId ?? null,
              preselectedVendorName: comp.vendorName ?? null,
              supplierUrl: comp.url,
            });
            continue;
          }
          if (comp.kind === 'third_party') {
            // Only the accepted vendor's row represents committed
            // sub-contracted work — the others are quote-comparison
            // entries that should never hit the basket. Vendor is
            // already snapshotted on the component, so no inline
            // picker is needed for these rows.
            if (comp.vendorQuoteStatus !== 'accepted') continue;
            if (!comp.vendorId) continue;
            const leadTime = (comp.leadTime || '').trim();
            const primary = `${
              svc.repairServiceName || svc.repairServiceCode
            }: ${comp.description || '(no description)'}${
              leadTime ? ` · Lead time: ${leadTime}` : ''
            }`;
            out.push({
              kind: 'repair_part',
              id: comp.id,
              primary,
              secondary: comp.productCode || 'no sku',
              preselectedVendorId: comp.vendorId,
              preselectedVendorName: comp.vendorName ?? null,
              supplierUrl: comp.url,
            });
          }
        }
      }
      return out;
    }

    for (const item of quote.items ?? []) {
      if (!item.selectedOptionId) continue;
      const opt = item.options.find((o) => o.id === item.selectedOptionId);
      if (!opt) continue;
      // Disti-sourced options snapshot their vendor; hand-typed / retail /
      // inventory parts (custom-build components) carry none, so the tech
      // picks one inline — an empty preselectedVendorId opens the per-row
      // vendor picker, exactly like repair parts.
      const distiVendorId = opt.sourceDistiId || null;
      out.push({
        kind: 'item',
        id: item.id,
        primary: `${item.name || '(unnamed item)'}: ${
          opt.description || '(no description)'
        }`,
        secondary: opt.productCode || 'no sku',
        preselectedVendorId: distiVendorId,
        preselectedVendorName: distiVendorId
          ? vendors.find((v) => v.vendorId === distiVendorId)?.name ?? null
          : null,
        supplierUrl: opt.url || null,
      });
    }
    return out;
  }, [quote, vendors, isServiceShaped]);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [vendorPick, setVendorPick] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendToBasketResponse | null>(null);

  // Vendor pool for the per-line picker. Active vendors only — including
  // distis and third-party suppliers, since a repair shop might order
  // parts from either kind.
  const vendorPool = useMemo(
    () => vendors.filter((v) => v.isActive),
    [vendors],
  );

  if (!canSend) return null;

  const openDialog = () => {
    setSelected(Object.fromEntries(eligible.map((e) => [e.id, true])));
    // Pre-fill the vendor picker for repair-part rows whose URL already
    // implies a vendor. The tech can still override per-row from the
    // autocomplete; this just removes the "pick the obvious vendor"
    // step when the URL is on a known retailer or matches a vendor by
    // code/name. Rows with a preselected vendor (items-flow, where the
    // option carries `sourceDistiId`) are unaffected.
    const initialPicks: Record<string, string> = {};
    for (const entry of eligible) {
      if (entry.preselectedVendorId) continue;
      if (!entry.supplierUrl) continue;
      const guess = lookupVendorByUrl(entry.supplierUrl, vendorPool);
      if (guess) initialPicks[entry.id] = guess.vendorId;
    }
    setVendorPick(initialPicks);
    setError(null);
    setResult(null);
    setOpen(true);
  };

  const toggleAll = (checked: boolean) => {
    setSelected(Object.fromEntries(eligible.map((e) => [e.id, checked])));
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      setError('Pick at least one line to send.');
      return;
    }
    const itemIds: string[] = [];
    const itemVendors: Record<string, string> = {};
    const repairCompIds: string[] = [];
    const repairVendors: Record<string, string> = {};
    const missingVendor: string[] = [];

    for (const e of eligible) {
      if (!selected[e.id]) continue;
      // Both shapes resolve the vendor the same way: the row's inline pick
      // wins, else the snapshotted/preselected vendor (disti items,
      // accepted third-party). No vendor → the line can't be ordered.
      const vid = vendorPick[e.id] || e.preselectedVendorId || '';
      if (!vid) {
        missingVendor.push(e.id);
        continue;
      }
      if (e.kind === 'item') {
        itemIds.push(e.id);
        itemVendors[e.id] = vid;
        continue;
      }
      repairCompIds.push(e.id);
      repairVendors[e.id] = vid;
    }

    if (missingVendor.length > 0) {
      setError(
        `Pick a vendor for ${missingVendor.length} line${
          missingVendor.length === 1 ? '' : 's'
        } before submitting.`,
      );
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const body: SendFromQuoteRequest = { quote_id: quote.id };
      if (itemIds.length > 0) {
        body.quote_item_ids = itemIds;
        body.quote_item_vendors = itemVendors;
      }
      if (repairCompIds.length > 0) {
        body.repair_component_ids = repairCompIds;
        body.repair_component_vendors = repairVendors;
      }
      const response = await sendFromQuote(body);
      setResult(response);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  const noneEligible = eligible.length === 0;
  const allSelected =
    eligible.length > 0 && eligible.every((e) => selected[e.id]);
  const emptyTooltip = isServiceShaped
    ? 'No parts marked as needing ordering'
    : 'No parts with a selected option to order';

  return (
    <>
      <Tooltip
        title={
          noneEligible
            ? emptyTooltip
            : 'Push parts to the part-orders basket'
        }
      >
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LocalShippingIcon />}
            disabled={noneEligible}
            onClick={openDialog}
          >
            Send to ordering
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send lines to part-orders basket</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {result && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Created {result.created.length} basket line
              {result.created.length === 1 ? '' : 's'}.
              {result.skipped.length > 0 &&
                ` ${result.skipped.length} skipped.`}
            </Alert>
          )}

          {!result && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={
                      !allSelected && selectedIds.length > 0
                    }
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                }
                label="Send all eligible"
              />
              <Stack spacing={1} sx={{ pl: 2, mt: 1 }}>
                {eligible.map((entry) => {
                  const needsVendor = entry.preselectedVendorId == null;
                  const pickedVendorId = vendorPick[entry.id] ?? '';
                  const pickedVendor =
                    vendorPool.find((v) => v.vendorId === pickedVendorId) ??
                    null;
                  return (
                    <Stack
                      key={entry.id}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <FormControlLabel
                        sx={{ flex: 1, alignItems: 'flex-start' }}
                        control={
                          <Checkbox
                            checked={!!selected[entry.id]}
                            onChange={() => toggleOne(entry.id)}
                          />
                        }
                        label={
                          <Stack sx={{ pt: 0.5 }}>
                            <Typography variant="body2">
                              {entry.primary}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {entry.secondary}
                              {entry.preselectedVendorName
                                ? ` · ${entry.preselectedVendorName}`
                                : ''}
                            </Typography>
                            {needsVendor && entry.supplierUrl && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 280,
                                }}
                              >
                                {entry.supplierUrl}
                              </Typography>
                            )}
                          </Stack>
                        }
                      />
                      {needsVendor && (
                        <Autocomplete
                          size="small"
                          options={vendorPool}
                          value={pickedVendor}
                          onChange={(_, v) =>
                            setVendorPick((prev) => ({
                              ...prev,
                              [entry.id]: v?.vendorId ?? '',
                            }))
                          }
                          getOptionLabel={(o) => (o ? o.name : '')}
                          isOptionEqualToValue={(o, v) =>
                            o.vendorId === v.vendorId
                          }
                          sx={{ width: 200, flexShrink: 0, mt: 1 }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Vendor"
                              placeholder="Pick…"
                            />
                          )}
                        />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </>
          )}

          {result && result.skipped.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Skipped
              </Typography>
              {result.skipped.map((s) => (
                <Typography
                  key={s.quoteItemId}
                  variant="caption"
                  component="div"
                  color="text.secondary"
                >
                  {s.quoteItemId.slice(0, 8)}: {s.reason}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting || selectedIds.length === 0}
            >
              {submitting ? 'Sending…' : `Send ${selectedIds.length}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
